import React, { useState } from 'react';
import { UserGroupIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import { DocumentNode } from '../../types/documents';

interface Collaborator {
  id: string;
  name: string;
  avatarBase64?: string;
  status: 'editing' | 'viewing' | 'offline';
}

interface Version {
  id: string;
  version: string;
  user: Collaborator;
  action: string;
  timestamp: string;
  isCurrent?: boolean;
}

interface Comment {
  id: string;
  user: Collaborator;
  content: string;
  timestamp: string;
}

interface DocumentCollaborationProps {
  selectedNode: DocumentNode | null;
}

export const DocumentCollaboration: React.FC<DocumentCollaborationProps> = ({
  selectedNode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newComment, setNewComment] = useState('');

  // Mock data para demonstração
  const collaborators: Collaborator[] = [
    { id: '1', name: 'Admin (você)', avatarBase64: 'https://randomuser.me/api/portraits/men/1.jpg', status: 'editing' },
    { id: '2', name: 'Maria Silva', avatarBase64: 'https://randomuser.me/api/portraits/women/1.jpg', status: 'viewing' },
    { id: '3', name: 'João Santos', avatarBase64: 'https://randomuser.me/api/portraits/men/2.jpg', status: 'offline' },
  ];
  const mockVersions: Version[] = [
    {
      id: '1',
      version: 'v1.0',
      user: collaborators[0],
      action: 'Criação do documento',
      timestamp: '23/04/2025 14:00',
    },
    {
      id: '2',
      version: 'v1.1',
      user: collaborators[0],
      action: 'Edição de conteúdo',
      timestamp: '23/04/2025 14:05',
    },
    {
      id: '3',
      version: 'Atual',
      user: collaborators[0],
      action: 'Formatação atualizada',
      timestamp: '23/04/2025 14:10',
      isCurrent: true,
    },
  ];
  const mockComments: Comment[] = [
    {
      id: '1',
      user: collaborators[0],
      content: 'Documento criado para registro financeiro do Q2.',
      timestamp: '23/04/2025 14:00',
    },
    {
      id: '2',
      user: collaborators[1],
      content: 'Por favor, adicionar as projeções de vendas quando disponíveis.',
      timestamp: '23/04/2025 14:15',
    },
  ];

  if (!selectedNode) return null;

  // Só exibe para arquivos .edoc
  const fileName = selectedNode.metadata?.fileName || selectedNode.title || '';
  const isEdoc = fileName.toLowerCase().endsWith('.edoc');
  if (!(selectedNode.nodeType === 'file' || selectedNode.nodeType === 'document') || !isEdoc) {
    return null;
  }

  // Botão flutuante recolhido
  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="fixed right-4 top-1/2 z-40 transform -translate-y-1/2 bg-blue-600 text-white rounded-full shadow-lg p-3 flex items-center hover:bg-blue-700 transition-all"
        title="Colaboração"
        style={{ minWidth: 0 }}
      >
        <UserGroupIcon className="h-6 w-6" />
      </button>
    );
  }

  // Painel expandido
  return (
    <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200 animate-slide-in">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-bold">Histórico e Colaboração</h2>
        <button
          onClick={() => setIsExpanded(false)}
          className="text-gray-500 hover:text-gray-700 rounded-full p-1"
          title="Fechar"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Colaboradores */}
      <div className="p-4 border-b">
        <div className="flex items-center mb-2">
          {collaborators.map((colab) => (
            <img
              key={colab.id}
              src={colab.avatarBase64}
              alt={colab.name}
              className="h-8 w-8 rounded-full border-2 border-white -ml-2 first:ml-0 shadow"
              title={colab.name}
            />
          ))}
          <button className="ml-3 px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium flex items-center gap-1">
            <PlusIcon className="h-4 w-4" />
            Convidar
          </button>
        </div>
        <ul className="text-xs text-gray-700 space-y-1">
          <li><span className="text-green-500 font-bold">●</span> Admin (você) - Editando agora</li>
          <li><span className="text-blue-400 font-bold">●</span> Maria Silva - Visualizando</li>
          <li><span className="text-gray-400 font-bold">●</span> João Santos - Offline</li>
        </ul>
      </div>

      {/* Histórico de Alterações */}
      <div className="p-4 border-b">
        <h3 className="text-base font-semibold mb-2">Histórico de Alterações</h3>
        <div className="space-y-2">
          {mockVersions.map((version) => (
            <div key={version.id} className={`flex items-center justify-between rounded px-3 py-2 border ${version.isCurrent ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <div>
                <div className="text-sm font-medium">{version.action}</div>
                <div className="text-xs text-gray-500">{version.user.name} - {version.timestamp}</div>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${version.isCurrent ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{version.version}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Comentários */}
      <div className="flex-1 flex flex-col p-4 overflow-y-auto">
        <h3 className="text-base font-semibold mb-2">Comentários</h3>
        <div className="flex-1 overflow-y-auto space-y-3 mb-3">
          {mockComments.map((comment) => (
            <div key={comment.id} className="flex items-start space-x-2">
              <img src={comment.user.avatarBase64} alt={comment.user.name} className="h-7 w-7 rounded-full mt-1" />
              <div className="bg-gray-100 rounded-lg px-3 py-2 flex-1">
                <div className="text-sm font-semibold">{comment.user.name}</div>
                <div className="text-xs text-gray-700 mb-1">{comment.content}</div>
                <div className="text-xs text-gray-400">{comment.timestamp}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center mt-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Adicionar comentário..."
            className="flex-1 border rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 text-sm flex items-center"
            title="Enviar"
          >
            <ArrowRightCircleIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}; 